import React from "react";
import { Route } from "react-router-dom";
import ModuleGate from "./ModuleGate";
import { DMS_PERMISSIONS, FIN_PERMISSIONS, PROC_PERMISSIONS } from "../../constants/platformModules";
import Approvals from "../Approvals/Approvals";
import FinanceSetup from "../Fin/FinanceSetup";
import FinJournals from "../Fin/FinJournals";
import ChartOfAccounts from "../Fin/ChartOfAccounts";
import FinProjects from "../Fin/FinProjects";
import FinPeriods from "../Fin/FinPeriods";
import FinReports from "../Fin/FinReports";
import ErpIntegration from "../Fin/ErpIntegration";
import DmsSetup from "../Dms/DmsSetup";
import ProcSetup from "../Proc/ProcSetup";
import ProcSuppliers from "../Proc/Suppliers";
import ProcRfqs from "../Proc/Rfqs";
import ProcRfqDetail from "../Proc/RfqDetail";
import ProcRfqForm from "../Proc/RfqForm";
import ProcMessages from "../Proc/Messages";
import ProcOrders from "../Proc/Orders";
import ProcOrderDetail from "../Proc/OrderDetail";
import ProcAwards from "../Proc/Awards";
import ProcDashboard from "../Proc/Dashboard";
import Receivables from "../Fin/Receivables";
import CashBank from "../Fin/CashBank";
import Payables from "../Fin/Payables";
import Purchasing from "../Dms/sales/Purchasing";
import PartsSales from "../Dms/parts/PartsSales";
import RepairOrders from "../Dms/workshop/RepairOrders";
import TechnicianBoard from "../Dms/workshop/TechnicianBoard";
import WarrantyClaims from "../Dms/workshop/WarrantyClaims";
import FiDeals from "../Dms/fi/FiDeals";
import { Recalls, ServiceReminders, CsiSurveys } from "../Dms/aftercare/Aftercare";
import AftersalesDashboard from "../Dms/aftercare/AftersalesDashboard";
import FleetSales from "../Dms/fleet/FleetSales";
import BodyShop from "../Dms/bodyshop/BodyShop";
import FinDashboard from "../Fin/FinDashboard";
import BankReconciliation from "../Fin/BankReconciliation";
import TaxReturns from "../Fin/TaxReturns";
import FixedAssets from "../Fin/FixedAssets";
import CounterSales from "../Dms/sales/CounterSales";
import SalesDashboard from "../Dms/sales/SalesDashboard";
import Quotations from "../Dms/sales/Quotations";
import SalesOrders from "../Dms/sales/SalesOrders";
import { GoodsTransfers, PdiRequests, VehicleAvailability, VehicleStock } from "../Dms/sales/StockPages";
import { ReturnOrders, TestDrives, TradeIns } from "../Dms/sales/SalesSupport";

const FIN_ANY = Object.values(FIN_PERMISSIONS);
const DMS_ANY = Object.values(DMS_PERMISSIONS);
const PROC_ANY = Object.values(PROC_PERMISSIONS);
const SELLERS = ["DMS_SALES", "DMS_SALES_MANAGER", "DMS_ADMIN"];

// The Finance / Dealership / Procurement route tree and the unified approvals inbox. A fragment of
// <Route>s (not a component) so it can sit inside <Routes> in App.js, like hrRoutes.
export const platformRoutes = (
  <>
    {/* Every tenant user; the inbox only ever shows what is theirs to see. */}
    <Route element={<ModuleGate />}>
      <Route path="/approvals" element={<Approvals />} />
    </Route>
    <Route element={<ModuleGate module="FIN" permission={FIN_ANY} />}>
      <Route path="/fin/receivables" element={<Receivables />} />
      <Route path="/fin/payables" element={<Payables />} />
      <Route path="/fin/journals" element={<FinJournals />} />
      <Route path="/fin/chart-of-accounts" element={<ChartOfAccounts />} />
      <Route path="/fin/projects" element={<FinProjects />} />
      <Route path="/fin/periods" element={<FinPeriods />} />
      <Route path="/fin/reports" element={<FinReports />} />
    </Route>
    <Route element={<ModuleGate module="FIN" permission={[...FIN_ANY, "DMS_CASHIER", "DMS_SALES_MANAGER", "DMS_ADMIN"]} />}>
      <Route path="/fin/cash" element={<CashBank />} />
    </Route>
    <Route element={<ModuleGate module="FIN" admin />}>
      <Route path="/fin/setup" element={<FinanceSetup />} />
    </Route>
    <Route element={<ModuleGate module="FIN" feature="ERP_CONNECTOR" admin />}>
      <Route path="/fin/erp" element={<ErpIntegration />} />
    </Route>
    <Route element={<ModuleGate module="DMS" admin />}>
      <Route path="/dms/setup" element={<DmsSetup />} />
    </Route>
    <Route element={<ModuleGate module="PROC" admin />}>
      <Route path="/proc/setup" element={<ProcSetup />} />
    </Route>
    <Route element={<ModuleGate module="PROC" permission={PROC_ANY} />}>
      <Route path="/proc/suppliers" element={<ProcSuppliers />} />
      <Route path="/proc/dashboard" element={<ProcDashboard />} />
      <Route path="/proc/rfqs" element={<ProcRfqs />} />
      <Route path="/proc/rfqs/:id" element={<ProcRfqDetail />} />
      <Route path="/proc/messages" element={<ProcMessages />} />
      <Route path="/proc/orders" element={<ProcOrders />} />
      <Route path="/proc/orders/:id" element={<ProcOrderDetail />} />
      <Route path="/proc/awards" element={<ProcAwards />} />
    </Route>
    <Route element={<ModuleGate module="PROC" permission={["PROC_ADMIN", "PROC_BUYER"]} />}>
      <Route path="/proc/rfqs/new" element={<ProcRfqForm />} />
      <Route path="/proc/rfqs/:id/edit" element={<ProcRfqForm />} />
    </Route>
    <Route element={<ModuleGate module="DMS" permission={SELLERS} />}>
      <Route path="/dms/dashboard" element={<SalesDashboard />} />
      <Route path="/dms/quotes" element={<Quotations />} />
      <Route path="/dms/orders" element={<SalesOrders />} />
      <Route path="/dms/test-drives" element={<TestDrives />} />
      <Route path="/dms/trade-ins" element={<TradeIns />} />
    </Route>
    <Route element={<ModuleGate module="DMS" permission={DMS_ANY} />}>
      <Route path="/dms/availability" element={<VehicleAvailability />} />
      <Route path="/dms/vehicles" element={<VehicleStock />} />
      <Route path="/dms/pdi" element={<PdiRequests />} />
      <Route path="/dms/counter-sales" element={<CounterSales />} />
    </Route>
    <Route element={<ModuleGate module="DMS" permission={["DMS_SALES_MANAGER", "DMS_ADMIN", "DMS_PARTS"]} />}>
      <Route path="/dms/purchasing" element={<Purchasing />} />
      <Route path="/dms/parts" element={<PartsSales />} />
      <Route path="/dms/repair-orders" element={<RepairOrders />} />
      <Route path="/dms/technician-board" element={<TechnicianBoard />} />
      <Route path="/dms/warranty-claims" element={<WarrantyClaims />} />
      <Route path="/dms/fi-deals" element={<FiDeals />} />
      <Route path="/dms/recalls" element={<Recalls />} />
      <Route path="/dms/reminders" element={<ServiceReminders />} />
      <Route path="/dms/csi" element={<CsiSurveys />} />
      <Route path="/dms/aftersales-dashboard" element={<AftersalesDashboard />} />
      <Route path="/dms/fleet" element={<FleetSales />} />
      <Route path="/dms/bodyshop" element={<BodyShop />} />
      <Route path="/fin/dashboard" element={<FinDashboard />} />
      <Route path="/fin/bank-reconciliation" element={<BankReconciliation />} />
      <Route path="/fin/tax-returns" element={<TaxReturns />} />
      <Route path="/fin/fixed-assets" element={<FixedAssets />} />
    </Route>
    <Route element={<ModuleGate module="DMS" permission={["DMS_SALES_MANAGER", "DMS_ADMIN"]} />}>
      <Route path="/dms/transfers" element={<GoodsTransfers />} />
      <Route path="/dms/returns" element={<ReturnOrders />} />
    </Route>
  </>
);

export default platformRoutes;
